import type { OrganizationOverviewData, SummaryData } from '@/shared/types/overviewTypes';
import {
  monthKeyFromStartDate,
} from '@/domains/global-dashboard/utils/chart-utils';
import { TerminalType } from '@/shared/types/userTerminalTypes';
import { getTerminalTypeChartFill } from '@/shared/utils/color-utils-terminals';

/** Bottom → top stack order for per-market terminal-type bars (chart view). */
export const TERMINAL_TYPE_STACK: { type: TerminalType; dataKey: string; fill: string }[] = [
  { type: TerminalType.STARLINK_PERFORMANCE_GEN_3, dataKey: 'ttGen3', fill: getTerminalTypeChartFill(TerminalType.STARLINK_PERFORMANCE_GEN_3) },
  { type: TerminalType.STARLINK_PERFORMANCE_GEN_2, dataKey: 'ttGen2', fill: getTerminalTypeChartFill(TerminalType.STARLINK_PERFORMANCE_GEN_2) },
  { type: TerminalType.STARLINK_ENTERPRISE_V4, dataKey: 'ttEnt', fill: getTerminalTypeChartFill(TerminalType.STARLINK_ENTERPRISE_V4) },
  { type: TerminalType.STARLINK_MINI, dataKey: 'ttMini', fill: getTerminalTypeChartFill(TerminalType.STARLINK_MINI) },
  { type: TerminalType.STARLINK_STANDARD_4, dataKey: 'ttStandard4', fill: getTerminalTypeChartFill(TerminalType.STARLINK_STANDARD_4) },
  { type: TerminalType.STARLINK_STANDARD_4X, dataKey: 'ttStandard4X', fill: getTerminalTypeChartFill(TerminalType.STARLINK_STANDARD_4X) },
];

/** Get the number of terminals by type from the summary data. */
export function terminalTypeCount(
  terminalTypes: { type: TerminalType; total: number; deployed: number }[] | null | undefined,
  type: TerminalType,
): { type: TerminalType; total: number; deployed: number } {
  if (!terminalTypes?.length) return { type: type, total: 0, deployed: 0 };
  const typeInfo = terminalTypes.find((t) => t.type === type);
  if (!typeInfo) return { type: type, total: 0, deployed: 0 };
  return {
    type: typeInfo.type,
    total: typeInfo.total,
    deployed: typeInfo.deployed,
  };
}

/** Slot for a terminal type in a month snapshot. */
type TerminalTypeSlot = { type: TerminalType; total: number; deployed: number; standby: number };

/** Snapshot of terminal inventory by month. */
export type TerminalMonthSnap = {
  total: number;
  deployed: number;
  standby: number;
  typeSlots: TerminalTypeSlot[];
};

/** Create a terminal month snapshot from a summary row. */
function snapshotTerminalMonthFromSummary(summary: SummaryData): TerminalMonthSnap {
  const typesArr = summary.terminalsSummary?.types ?? [];
  const typeSlots = TERMINAL_TYPE_STACK.map(({ type }) => {
    const c = terminalTypeCount(typesArr, type);
    return { type, total: c.total, deployed: c.deployed, standby: Math.max(0, c.total - c.deployed) };
  });
  const total = summary.terminalsSummary?.total ?? 0;
  const deployed = summary.terminalsSummary?.deployed ?? 0;
  const standby = Math.max(0, total - deployed);
  return {
    total,
    deployed,
    standby,
    typeSlots,
  };
}

/** Map month key → snapshot for overview `summary[]`. */
export function terminalMonthsByYmFromOverview(organization: OrganizationOverviewData): Map<string, TerminalMonthSnap> {
  const map = new Map<string, TerminalMonthSnap>();
  if (!organization.summary?.length) return map;
  for (const row of organization.summary) {
    map.set(monthKeyFromStartDate(row.startDate), snapshotTerminalMonthFromSummary(row));
  }
  return map;
}